import { EXPERIENCE_ID } from '@atlaskit/editor-common/experiences/consts';
import { Experience } from '@atlaskit/editor-common/experiences/Experience';
import { ExperienceCheckTimeout } from '@atlaskit/editor-common/experiences/ExperienceCheckTimeout';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';

export const ACCEPT_SUGGESTION_TIMEOUT_MS = 30_000;

export type AcceptSuggestionExperience = {
	isAccepted: (id: string) => boolean;
	plugin: () => SafePlugin<void>;
	start: (method: string) => Experience;
};

/** Tracks independent accept attempts until document application and status update complete. */
export function createAcceptSuggestionExperience(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): AcceptSuggestionExperience {
	const pending = new Set<Experience>();
	let editorView: EditorView | undefined;
	return {
		isAccepted: (id) => {
			const state = editorView && aiSuggestionsPluginKey.getState(editorView.state);
			// execute can return true even when the transaction was filtered.
			return !!state && !state.suggestionById.has(id);
		},
		start: (method) => {
			const experience = new Experience(EXPERIENCE_ID.MENU_ACTION, {
				action: 'acceptSuggestion',
				actionSubjectId: 'aiSuggestions',
				sampleRate: 1,
				checks: [new ExperienceCheckTimeout({ durationMs: ACCEPT_SUGGESTION_TIMEOUT_MS })],
				dispatchAnalyticsEvent: (event) => {
					if (event.attributes?.experienceStatus !== 'started') {
						pending.delete(experience);
					}
					api?.analytics?.actions.fireAnalyticsEvent(event);
				},
			});
			pending.add(experience);
			experience.start({ method });
			return experience;
		},
		plugin: () =>
			new SafePlugin<void>({
				view: (view) => {
					editorView = view;
					return {
						destroy: () => {
							for (const experience of pending) {
								experience.abort({ reason: 'editorDestroyed' });
							}
							editorView = undefined;
						},
					};
				},
			}),
	};
}
